import React, { useState, useEffect } from 'react';
import { Sparkles, Send } from 'lucide-react';

const SAMPLE_PROMPTS = [
  "What's the completion rate for my survey?",
  "Show me the demographics breakdown",
  "Which questions have the lowest response rate?",
  "How many respondents completed section 2?",
  "What are the most common answers to question 5?"
];

export default function ChatbotFeature({featureName}) {
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fullPrompt = SAMPLE_PROMPTS[promptIndex];
    
    const typingSpeed = isDeleting ? 30 : 80;
    const pauseBeforeDelete = 2000;
    const pauseBeforeNext = 500;

    if (!isDeleting && charIndex >= fullPrompt.length) {
      // Finished typing, pause then start deleting
      const timeout = setTimeout(() => setIsDeleting(true), pauseBeforeDelete);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && charIndex <= 0) {
      // Finished deleting, move to next prompt
      setIsDeleting(false);
      setCharIndex(0);
      const timeout = setTimeout(() => {
        setPromptIndex((prev) => (prev + 1) % SAMPLE_PROMPTS.length);
      }, pauseBeforeNext);
      return () => clearTimeout(timeout);
    }

    // Type or delete one character
    const timeout = setTimeout(() => {
      if (isDeleting) {
        setCharIndex((prev) => Math.max(0, prev - 1));
        setCurrentPrompt(fullPrompt.substring(0, Math.max(0, charIndex - 1)));
      } else {
        setCharIndex((prev) => Math.min(fullPrompt.length, prev + 1));
        setCurrentPrompt(fullPrompt.substring(0, Math.min(fullPrompt.length, charIndex + 1)));
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, promptIndex]);

  return (
    <div className="mx-auto px-6 py-12">
      <div className="flex flex-col items-center justify-center min-h-[400px]" style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <Sparkles className="h-16 w-16 mb-4" style={{ color: 'var(--color-primary)' }} />
        <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
          Chat with my survey
        </h2>
        <p className="text-lg mb-8" style={{ color: 'var(--color-muted)' }}>
          Coming very soon 🤗
        </p>

        {/* Simulated Chat Interface */}
        <div 
          className="w-full rounded-lg shadow-lg overflow-hidden"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid',
            borderColor: 'color-mix(in srgb, var(--color-muted) 30%, transparent)'
          }}
        >
          {/* Chat Header */}
          <div 
            className="px-4 py-3 border-b flex items-center gap-2"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--color-primary) 5%, transparent)',
              borderColor: 'color-mix(in srgb, var(--color-muted) 30%, transparent)'
            }}
          >
            <div 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: 'var(--color-primary)' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              Survey AI Assistant
            </span>
          </div>

          {/* Chat Messages Area */}
          <div className="p-4 space-y-4" style={{ minHeight: '200px' }}>
            {/* AI Welcome Message */}
            <div className="flex gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <Sparkles className="h-4 w-4" style={{ color: 'white' }} />
              </div>
              <div 
                className="rounded-lg px-4 py-2 max-w-[80%]"
                style={{ 
                  backgroundColor: 'color-mix(in srgb, var(--color-muted) 20%, transparent)',
                  color: 'var(--color-text)'
                }}
              >
                <p className="text-sm">
                  Hi! I'm your survey AI assistant. Ask me anything about your survey data, 
                  completion rates, or respondent insights.
                </p>
              </div>
            </div>
          </div>

          {/* Input Area with Typing Animation */}
          <div 
            className="border-t px-4 py-3"
            style={{ 
              borderColor: 'color-mix(in srgb, var(--color-muted) 30%, transparent)',
              backgroundColor: 'var(--color-bg)'
            }}
          >
            <div className="flex items-center gap-2">
              <div 
                className="flex-1 px-4 py-2 rounded-lg flex items-center"
                style={{ 
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid',
                  borderColor: 'color-mix(in srgb, var(--color-primary) 30%, transparent)'
                }}
              >
                <span className="text-sm" style={{ color: 'var(--color-text)' }}>
                  {currentPrompt}
                  <span 
                    className="inline-block w-0.5 h-4 ml-0.5 animate-pulse"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  />
                </span>
              </div>
              <button 
                className="p-2 rounded-lg transition-opacity"
                style={{ 
                  backgroundColor: 'var(--color-primary)',
                  opacity: 0.5
                }}
                disabled
              >
                <Send className="h-5 w-5" style={{ color: 'white' }} />
              </button>
            </div>
          </div>
        </div>

        <p className="text-xs mt-4" style={{ color: 'var(--color-muted)' }}>
          Preview only - Interactive chat coming soon
        </p>
      </div>
    </div>
  );
}